import config from '../utils/config';
import { injectSocialTags } from '../utils/socialTags';
import { isLegacyPlaceholderLogoUrl } from '../utils/questionnaireBranding';
import {
  TREATMENT_API_AUTH_SCHEME,
  TREATMENT_API_PATH,
} from '../features/treatment-flow/treatmentApiConstants';
import {
  API_PATH,
  joinApiEndpoint,
  normalizeApiV1Base,
} from '../constants/apiEndpoints';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

class ClientConfigService {
  constructor() {
    this.config = null;
    this.loading = false;
    this.cache = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    this.embedToken = null;
    this.embedTokenClientId = null;
    this.tokenExpiry = null;
    this.adminTokenCache = null;
    this.adminTokenExpiry = null;
    this.lastLoadError = null;
    this.configPromises = new Map();
  }

  createRequestError(data, fallbackMessage) {
    const error = new Error(data?.error || data?.message || fallbackMessage);
    error.code = data?.code;
    error.brandName = data?.brand_name;
    return error;
  }

  createRuntimeResponseError(data, status, fallbackMessage) {
    const error = new Error(
      data?.detail || data?.error || data?.message || fallbackMessage,
    );
    error.code = data?.code || data?.error;
    error.status = status;
    error.details = data?.details;
    error.rawCode = data?.error;
    error.retryable = data?.retryable;
    return error;
  }

  async getClientConfig(clientId) {
    if (!clientId) {
      throw new Error('Client ID is required');
    }

    // Check cache first
    const cached = this.cache.get(clientId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('📁 Using cached client config for:', clientId);
      return cached.config;
    }

    const pending = this.configPromises.get(clientId);
    if (pending) {
      console.log('⏳ Waiting for in-flight client config request:', clientId);
      return pending;
    }

    const requestPromise = (async () => {
      console.log('🔍 Fetching secure client configuration for:', clientId);

      // Get embed token (environment-aware)
      const embedToken = await this.getEmbedToken(clientId);

      // Use embed token to fetch client config
      const response = await fetchWithTimeout(`${config.API_BASE_URL}/config/${clientId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${embedToken}`,
          'Origin': window.location.origin
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw this.createRequestError(
          errorData,
          `Config request failed (${response.status})`
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load client configuration');
      }

      if (!data.client_config || !data.client_config.id) {
        throw new Error('Client configuration response is missing client details');
      }

      this.config = data.client_config;
      localStorage.setItem(
        'welliemd_address_proxy_enabled',
        this.config.address_proxy_enabled ? '1' : '0',
      );

      // Cache the result
      this.cache.set(clientId, {
        config: this.config,
        timestamp: Date.now()
      });

      console.log('✅ Secure client configuration loaded:', {
        clientId: this.config.id,
        name: this.config.name,
        templateId: this.config.default_template_id
      });

      console.log('📦 Tracking JS in response:', {
        custom_global_js: this.config.custom_global_js ? `${this.config.custom_global_js.substring(0, 50)}...` : 'empty',
        conversion_tracking_js: this.config.conversion_tracking_js ? `${this.config.conversion_tracking_js.substring(0, 50)}...` : 'empty'
      });

      // NOTE: Social tags are primarily injected from visit config (getVisitConfig) if available
      // However, if visit config is not called (no visit type), we should inject from client config
      // We'll inject here as a fallback, but visit config will override if it's called
      // The visit config endpoint includes the social tags, so it takes precedence
      try {
        if (this.config.custom_global_js || this.config.conversion_tracking_js) {
          console.log('🔧 Injecting tracking JS from client config (visit config will override if available)');
          if (document && document.head) {
            await injectSocialTags(this.config);
          } else {
            setTimeout(() => {
              if (document && document.head) {
                injectSocialTags(this.config).catch((error) => {
                  console.warn('⚠️ Failed to inject tracking JS from client config (delayed):', error);
                });
              }
            }, 100);
          }
        } else {
          console.log('ℹ️ No tracking JS in client config - will check visit config');
        }
      } catch (error) {
        console.warn('⚠️ Failed to inject social tags from client config:', error);
      }

      return this.config;

    })();

    this.configPromises.set(clientId, requestPromise);
    this.loading = true;

    try {
      return await requestPromise;
    } catch (error) {
      console.error('❌ Failed to load secure client configuration:', error);
      this.lastLoadError = error;
      throw error;
    } finally {
      this.configPromises.delete(clientId);
      this.loading = this.configPromises.size > 0;
    }
  }

  // 🔑 Environment-aware embed token acquisition
  async getEmbedToken(clientId) {
    // Check cached token
    if (
      this.embedToken
      && this.embedTokenClientId === clientId
      && this.tokenExpiry
      && Date.now() < this.tokenExpiry - 60000
    ) {
      console.log('🎫 Using cached embed token');
      return this.embedToken;
    }

    try {
      // Detect environment
      const isDevelopment = process.env.NODE_ENV === 'development' ||
        config.API_BASE_URL.includes('localhost') ||
        config.API_BASE_URL.includes('127.0.0.1');

      let tokenData;

      if (isDevelopment) {
        // 🖥️ DEVELOPMENT: Direct Django API call
        console.log('🛠️ Development mode: Getting embed token via Django API');
        tokenData = await this.getEmbedTokenDevelopment(clientId);
      } else {
        // ☁️ PRODUCTION: Call your backend service
        console.log('🚀 Production mode: Getting embed token via backend service');
        tokenData = await this.getEmbedTokenProduction(clientId);
      }

      // 🚀 FIXED: Cache in memory AND localStorage
      this.embedToken = tokenData.embed_token;
      this.embedTokenClientId = clientId;
      this.tokenExpiry = new Date(tokenData.expires_at).getTime();

      // ✅ Store in localStorage for API service
      localStorage.setItem('welliemd_embed_token', tokenData.embed_token);
      localStorage.setItem('welliemd_embed_token_expires', tokenData.expires_at);

      console.log('✅ Embed token obtained and stored in localStorage:', {
        expiresAt: tokenData.expires_at,
        mode: isDevelopment ? 'development' : 'production',
        storedInLocalStorage: true
      });

      return this.embedToken;

    } catch (error) {
      console.error('❌ Failed to get embed token:', error);
      if (error.code === 'client_inactive') {
        throw error;
      }
      throw new Error(`Token acquisition failed: ${error.message}`);
    }
  }

  // 🖥️ Development: Direct Django API with demo admin
  async getEmbedTokenDevelopment(clientId) {
    // Get admin token
    const adminToken = await this.getDemoAdminToken();

    // Request embed token from Django
    const response = await fetchWithTimeout(`${config.API_BASE_URL}/clients/${clientId}/embed-token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`
      },
      body: JSON.stringify({
        origin: window.location.origin,
        expiry_minutes: 30
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw this.createRequestError(
        errorData,
        `Django embed token request failed (${response.status})`
      );
    }

    return await response.json();
  }

  // ☁️ Production: Call your backend service (Express/Node.js)
  async getEmbedTokenProduction(clientId) {
    // This would call your own backend service
    // You'll need to implement this endpoint on your server
    const response = await fetchWithTimeout(joinApiEndpoint(window.location.origin, API_PATH.embedToken), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        client_id: clientId,
        origin: window.location.origin
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw this.createRequestError(
        errorData,
        `Backend embed token request failed (${response.status})`
      );
    }

    return await response.json();
  }

  // 🔐 Get admin token for development (cached)
  async getDemoAdminToken() {
    // Check cache
    if (this.adminTokenCache && this.adminTokenExpiry && Date.now() < this.adminTokenExpiry - 300000) {
      console.log('🎫 Using cached admin token');
      return this.adminTokenCache;
    }

    console.log('🔐 Getting demo admin token...');

    const response = await fetchWithTimeout(`${config.API_BASE_URL}/auth/token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: 'admin',
        password: 'admin22443'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Admin login failed: ${errorData.detail || response.status}`);
    }

    const data = await response.json();

    if (!data.access) {
      throw new Error('No access token received from admin login');
    }

    // Cache admin token
    this.adminTokenCache = data.access;
    this.adminTokenExpiry = Date.now() + (55 * 60 * 1000); // 55 minutes

    console.log('✅ Demo admin token obtained and cached');
    return this.adminTokenCache;
  }

  async resolveClientByDomain(domain) {
    console.log('🌐 Resolving client by domain:', domain);

    try {
      const response = await fetchWithTimeout(`${config.API_BASE_URL}/clients/resolve/?domain=${encodeURIComponent(domain)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw this.createRequestError(
          errorData,
          `Domain resolution failed (${response.status})`
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to resolve domain');
      }

      console.log('✅ Client resolved from domain:', data.client);
      return data.client;

    } catch (error) {
      console.error('❌ Domain resolution failed:', error);
      throw error;
    }
  }

  async resolveClientByCheckoutToken(token) {
    const response = await fetchWithTimeout(
      `${config.API_BASE_URL}/clients/resolve-checkout/?token=${encodeURIComponent(token)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success || !data.client?.id) {
      throw this.createRequestError(data, 'This checkout link is unavailable.');
    }
    return data.client;
  }

  async getVisitConfig(clientId, visitType) {
    console.log('Fetching visit config:', { clientId, visitType });

    try {
      // Get API token from environment (CLIENT_WEBHOOK_TOKEN)
      const apiToken = process.env.REACT_APP_API_TOKEN || '';

      if (!apiToken) {
        throw new Error('API token not configured. Please contact support.');
      }

      // Use client-specific API endpoint
      const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');

      if (!clientApiEndpoint) {
        throw new Error('Client API endpoint not found. Please refresh the page.');
      }

      // Get client name from localStorage (cross-database compatible)
      const clientName = localStorage.getItem('welliemd_client_name');

      if (!clientName) {
        throw new Error('Client name not found. Please refresh the page.');
      }

      // Build URL - use client API endpoint for visit config
      const url = joinApiEndpoint(clientApiEndpoint, API_PATH.visitConfig(visitType));

      console.log('📍 Using client API for visit config:', url);
      console.log('📍 Client name:', clientName);

      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`,
          'X-Client-Name': clientName
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || `Visit config request failed (${response.status})`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load visit configuration');
      }

      console.log('✅ Visit config loaded:', data);
      console.log('🔍 Tracking JS check:', {
        hasCustomGlobalJs: 'custom_global_js' in data,
        customGlobalJsType: typeof data.custom_global_js,
        hasConversionTrackingJs: 'conversion_tracking_js' in data,
        conversionTrackingJsType: typeof data.conversion_tracking_js
      });

      // Extract templateId from response
      if (data.form_template?.id) {
        console.log('📋 Template ID from visit config:', data.form_template.id);
      }

      console.log('📦 Tracking JS in response:', {
        custom_global_js: data.custom_global_js ? (data.custom_global_js.length > 50 ? data.custom_global_js.substring(0, 50) + '...' : data.custom_global_js) : 'empty',
        conversion_tracking_js: data.conversion_tracking_js ? (data.conversion_tracking_js.length > 50 ? data.conversion_tracking_js.substring(0, 50) + '...' : data.conversion_tracking_js) : 'empty',
      });

      try {
        // Ensure document.head exists before injecting
        if (document && document.head) {
          console.log('🔧 Injecting tracking JS from visit config into <head>');
          await injectSocialTags({
            custom_global_js: typeof data.custom_global_js === 'string' ? data.custom_global_js : '',
            conversion_tracking_js: typeof data.conversion_tracking_js === 'string' ? data.conversion_tracking_js : '',
          });
        } else {
          console.log('⏳ Waiting for DOM to be ready before injecting tags');
          // Wait for DOM to be ready
          setTimeout(() => {
            if (document && document.head) {
              console.log('🔧 Injecting tracking JS from visit config into <head> (delayed)');
              injectSocialTags({
                custom_global_js: typeof data.custom_global_js === 'string' ? data.custom_global_js : '',
                conversion_tracking_js: typeof data.conversion_tracking_js === 'string' ? data.conversion_tracking_js : '',
              }).catch((error) => {
                console.error('❌ Failed to inject tracking JS from visit config (delayed):', error);
              });
            } else {
              console.error('❌ document.head still not available after delay');
            }
          }, 100);
        }
      } catch (error) {
        console.error('❌ Failed to inject tracking JS from visit config:', error);
        // Don't throw - tags are optional
      }

      return data;

    } catch (error) {
      console.error('❌ Failed to load visit config:', error);
      throw error;
    }
  }

  async getProgramPreview(programId, apiBaseUrl = null) {
    if (!programId) {
      throw new Error('Program ID is required');
    }

    const baseUrl = normalizeApiV1Base(apiBaseUrl || config.API_BASE_URL);
    const response = await fetchWithTimeout(`${baseUrl}${TREATMENT_API_PATH.programPreview(programId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.detail || `Program preview request failed (${response.status})`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to load program preview');
    }

    return data;
  }

  async getProgramRuntime(programId, apiBaseUrl = null) {
    if (!programId) {
      throw new Error('Program ID is required');
    }
    const baseUrl = normalizeApiV1Base(apiBaseUrl || config.API_BASE_URL);
    const response = await fetchWithTimeout(`${baseUrl}${TREATMENT_API_PATH.programRuntime(programId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw this.createRuntimeResponseError(
        errorData,
        response.status,
        `Program runtime request failed (${response.status})`,
      );
    }
    return response.json();
  }

  async getTreatmentPreviewCapability(capabilityToken, apiBaseUrl = null) {
    if (!capabilityToken) {
      throw new Error('Preview capability is required');
    }
    const baseUrl = normalizeApiV1Base(apiBaseUrl || config.API_BASE_URL);
    const response = await fetchWithTimeout(
      `${baseUrl}${TREATMENT_API_PATH.previewCapabilityResolve}`,
      {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${TREATMENT_API_AUTH_SCHEME.previewCapability} ${capabilityToken}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.detail || `Treatment preview request failed (${response.status})`
      );
      error.code = errorData.error || 'preview_load_failed';
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  async getTreatmentPreviewMatches(
    capabilityToken,
    payload,
    apiBaseUrl = null
  ) {
    if (!capabilityToken) {
      throw new Error('Preview capability is required');
    }
    const baseUrl = normalizeApiV1Base(apiBaseUrl || config.API_BASE_URL);
    const response = await fetchWithTimeout(
      `${baseUrl}${TREATMENT_API_PATH.previewCapabilityMatches}`,
      {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${TREATMENT_API_AUTH_SCHEME.previewCapability} ${capabilityToken}`,
        },
        body: JSON.stringify(payload || {}),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detailText = data.details && typeof data.details === 'object'
        ? Object.entries(data.details)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
          .join(' ')
        : '';
      const error = new Error(
        data.detail || detailText || 'Treatment preview matching failed'
      );
      error.code = data.error || 'preview_matching_failed';
      error.status = response.status;
      error.details = data.details;
      throw error;
    }
    return data;
  }

  getClientTreatmentPreviewUrl(path) {
    const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');
    const apiBaseUrl = normalizeApiV1Base(clientApiEndpoint || config.API_BASE_URL);
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${apiBaseUrl}${normalizedPath}`;
  }

  async getCustomProgramPreview(customProgramId, apiBaseUrl = null) {
    if (!customProgramId) {
      throw new Error('Custom program ID is required');
    }

    const baseUrl = normalizeApiV1Base(apiBaseUrl || config.API_BASE_URL);
    const previewUrl = `${baseUrl}${TREATMENT_API_PATH.customProgramPreview(customProgramId)}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    const response = await fetchWithTimeout(previewUrl, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.detail || `Custom program preview request failed (${response.status})`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to load custom program preview');
    }

    return data;
  }

  async getCustomProgramRuntime(customProgramId, apiBaseUrl = null) {
    if (!customProgramId) {
      throw new Error('Custom program ID is required');
    }

    const baseUrl = normalizeApiV1Base(apiBaseUrl || config.API_BASE_URL);
    const runtimeUrl = `${baseUrl}${TREATMENT_API_PATH.customProgramRuntime(customProgramId)}`;
    const response = await fetchWithTimeout(runtimeUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw this.createRuntimeResponseError(
        errorData,
        response.status,
        `Custom program runtime request failed (${response.status})`,
      );
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to load custom program runtime');
    }

    return data;
  }

  async getCustomProgramRecommendations(customProgramId, payload = {}) {
    if (!customProgramId) {
      throw new Error('Custom program ID is required');
    }

    const recommendationsUrl = this.getClientTreatmentPreviewUrl(
      TREATMENT_API_PATH.customProgramRecommendations(customProgramId)
    );
    const response = await fetchWithTimeout(recommendationsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.detail || `Custom program recommendations request failed (${response.status})`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to load custom program recommendations');
    }

    return data;
  }

  // Apply branding. Values are always written, including defaults, because a
  // preview can be opened after a branded runtime in the same document.
  // Leaving an old custom property in place makes the WellieMD admin preview
  // inherit the previous client's colours.
  applyBranding(clientConfig) {
    const root = document.documentElement;
    const questionnaire =
      clientConfig?.branding_config || clientConfig?.questionnaire_config || {};
    const primaryColor = questionnaire?.primary_color || '#222222';
    const accentColor = questionnaire?.accent_color || questionnaire?.secondary_color || '#F4D2B1';
    const fontFamily = questionnaire?.font_family
      || "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif";

    root.style.setProperty('--primary-color', primaryColor);
    root.style.setProperty('--secondary-color', accentColor);
    root.style.setProperty('--font-family', fontFamily);

    // The questionnaire design system uses the zyvgo-prefixed variables while
    // older screens still use the unprefixed variables above. Keep both in
    // sync so tenant branding is identical in live and preview journeys.
    root.style.setProperty('--zyvgo-primary', primaryColor);
    root.style.setProperty('--zyvgo-accent', accentColor);
    root.style.setProperty('--zyvgo-font-family', fontFamily);

    // Apply theme
    const theme = questionnaire?.theme || 'light';
    document.body.className = `theme-${theme}`;

    // Set theme colors
    if (theme === 'dark') {
      root.style.setProperty('--bg-color', '#1a1a1a');
      root.style.setProperty('--text-color', '#ffffff');
      root.style.setProperty('--border-color', '#444444');
    } else {
      root.style.setProperty('--bg-color', '#ffffff');
      root.style.setProperty('--text-color', '#2c3e50');
      root.style.setProperty('--border-color', '#e1e5e9');
    }

    const companyName = questionnaire?.company_name || clientConfig?.name;
    if (companyName) {
      document.title = `${companyName} - Health Assessment`;
    }

    // Apply favicon
    const rawFavicon = questionnaire?.logos?.favicon;
    let appliedFavicon = false;
    if (rawFavicon && typeof rawFavicon === 'string' && !isLegacyPlaceholderLogoUrl(rawFavicon)) {
      let href = rawFavicon.includes('localstack')
        ? rawFavicon.replace('localstack', 'localhost')
        : rawFavicon;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = href;
      appliedFavicon = true;
    }

    console.log('🎨 Applied client branding:', {
      theme,
      primaryColor,
      companyName,
      hasFavicon: appliedFavicon
    });
  }

  // 🚀 FIXED: Clear cache and tokens including localStorage
  clearCache(clientId = null) {
    if (clientId) {
      this.cache.delete(clientId);
    } else {
      this.cache.clear();
    }

    this.embedToken = null;
    this.embedTokenClientId = null;
    this.tokenExpiry = null;
    this.adminTokenCache = null;
    this.adminTokenExpiry = null;

    // ✅ Clear from localStorage too
    localStorage.removeItem('welliemd_embed_token');
    localStorage.removeItem('welliemd_embed_token_expires');
    localStorage.removeItem('welliemd_client_id');
    localStorage.removeItem('welliemd_client_name');
    localStorage.removeItem('welliemd_client_api_endpoint');
    localStorage.removeItem('welliemd_address_proxy_enabled');

    console.log('🧹 Cache and tokens cleared (including localStorage)');
  }
}

export default new ClientConfigService();
